<script>
const ShepherdLib = () => import(/* webpackChunkName: "tour-lib" */ 'shepherd.js')
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { STORAGE_KEYS } from '@/constants/storage'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import getVisitedTours from '@/utils/getVisitedTours'
import localStorage from '@/utils/localStorage'

/**
 * This component can be used for user handholding
 *
 */
export default {
  name: 'Tour',
  props: {
    /**
     *This is an array of handholding steps
     *
     e.g
     [{
     target: '#element-selector',
     title: 'Sample heading',
     content: 'Sample content. Can be HTML',
     placement: 'right',
     params: {}, // popper js params,
     size: 'md' //default: 'sm'
     }]
     */
    steps: Array,
    /**
     * This contain meta information about tour. These events are sent to matomo.
     *
     *  e.g {
     *        name: '', // unique tour name
     *        eventName: '', // name of the event to be sent to matomo (after completing, skipping tour)
     *      }
     */
    meta: Object
  },
  mixins: [MediaQueryMixin],
  computed: {
    tourName() {
      return this.meta?.name
    }
  },
  methods: {
    getButtonsForStep(step, stepIndex) {
      const buttons = []
      if (stepIndex === 0) {
        buttons.push({
          text: '',
          classes: 'hidden'
        })
      } else {
        buttons.push({
          text: 'Back',
          classes: 'btn btn-link text-medium-1 back-btn py-1',
          action: this.tour.back
        })
      }
      if (stepIndex !== this.steps.length - 1) {
        buttons.push({
          text: '<i class="close-white-icon"></i>',
          classes: 'skip-tour',
          action: this.tour.cancel
        })
      }
      buttons.push({
        text: stepIndex === this.steps.length - 1 ? 'Done' : 'Next',
        classes: 'btn btn-primary rounded-pill text-medium-1 py-1 px-4',
        action: this.tour.next
      })
      return buttons
    },
    markTourAsVisited() {
      const visitedTours = getVisitedTours()
      visitedTours.push(this.tourName)
      localStorage.setItem(STORAGE_KEYS.visitedTours, JSON.stringify(visitedTours))
    },
    bindTourEvents() {
      if (this.tourName) {
        this.tour.on('complete', () => {
          this.markTourAsVisited()
          trackEvent(EVENT_CATEGORY.TOUR, EVENT_ACTION.COMPLETE, this.meta.eventName)
          /**
           * @emits
           * This is emitted when tour is completed
           */
          this.$emit('complete')
        })
        this.tour.on('cancel', ({ index }) => {
          this.markTourAsVisited()
          trackEvent(
            EVENT_CATEGORY.TOUR,
            `${EVENT_ACTION.SKIP} (${index + 1})`,
            this.meta.eventName
          )
          /**
           * @emits
           * This is emitted when tour is cancelled
           */
          this.$emit('cancel')
        })
      }
    },
    isTourVisited() {
      const visitedTours = getVisitedTours()
      return visitedTours.includes(this.tourName)
    }
  },
  render() {
    return null
  },
  async mounted() {
    // Don't show tour on small devices
    if (this.isTourVisited() || this.isMobileMode) return
    const Shepherd = await ShepherdLib()
    this.tour = new Shepherd.default.Tour({
      defaultStepOptions: {
        scrollTo: false
      }
    })
    this.bindTourEvents()
    this.steps.forEach((step, stepIndex) => {
      const buttons = this.getButtonsForStep(step, stepIndex)
      this.tour.addStep({
        classes: `tour-step size-${step.size} ${step.classes}`,
        text: `<div class="tour-step-heading text-large bold text-left mb-3">${
          step.title
        }</div><div class="tour-step-content text-medium-1">${step.content}</div>`,
        attachTo: {
          element: step.target,
          on: step.placement
        },
        buttons,
        popperOptions: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: step.offset || [0, 15]
              }
            }
          ]
        },
        ...(step.params || {})
      })
    })
    this.tour.start()
  }
}
</script>

<style lang="scss">
@import '~@/styles/_styleConfigurations.scss';
@import '~shepherd.js/dist/css/shepherd.css';

.tour-step {
  background-color: $primary;
  box-shadow: $deepShadow;
  border-radius: 10px;
  min-width: 300px;
  color: $documentColor;
}

.tour-step[data-popper-placement='bottom-start'] .shepherd-arrow {
  left: 20px !important;
  transform: none !important;
}

.shepherd-arrow:before {
  background-color: $primary;
}

.shepherd-text {
  color: $documentColor;
  padding: 20px;
}

.shepherd-footer {
  padding: 0 20px 20px 20px;
  display: flex;
  justify-content: space-between;
}

.back-btn {
  color: $documentColor;
  text-decoration: underline;
  padding-left: 0;
  padding-right: 0;

  &.shepherd-button {
    background-color: transparent !important;
  }
}

.shepherd-button {
  color: $documentColor;
  background-color: $brand;
}

.shepherd-button.hidden {
  visibility: hidden;
  pointer-events: none;
}

.skip-tour {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 3px;
  margin: 0;
  line-height: 10px;
  background-color: $secondary !important;
  border-radius: 50%;

  &:hover {
    background-color: $documentColor !important;

    .close-white-icon {
      background-image: url(~@/assets/close.svg);
    }
  }
}

.shepherd-element {
  max-width: 310px;
}

.shepherd-element.size-md {
  max-width: 380px;
}

.z-index-auto {
  z-index: auto;
}
</style>
